// Wires sim <-> render <-> ui. Fixed-timestep sim loop; render runs every frame.
import balanceJson from '../data/balance.json';
import texasJson from '../data/maps/texas.json';
import { handleAudioEvents } from './audio';
import { createRenderApp } from './render/app';
import { createEntityLayer } from './render/entities';
import { createMapLayer } from './render/map';
import { createPaperLayer } from './render/paper';
import { palette } from './render/palette';
import { createSunLayer } from './render/sun';
import { Tweens } from './render/tween';
import {
  apply,
  computeDispatch,
  createWorld,
  tick,
  type Balance,
  type Command,
  type MapData,
} from './sim';
import { attachPointer, createController } from './ui/controller';
import { createCurveStrip } from './ui/curve';
import { showGameOver } from './ui/gameover';
import { createHud } from './ui/hud';
import { createShop } from './ui/shop';
import { createToast } from './ui/toast';

const balance = balanceJson as Balance;
const map = texasJson as MapData;

/** Real seconds per in-game day at 1x. Tunable; see DESIGN.md §12. */
const SECONDS_PER_DAY = 90;

async function main(): Promise<void> {
  const root = document.getElementById('app');
  if (!root) throw new Error('#app missing');

  const seed = Date.now() >>> 0;
  const world = createWorld(map, balance, seed);

  if (import.meta.env.DEV) {
    // Debug handle for the dev console only; never shipped.
    (window as unknown as { grid: unknown }).grid = {
      world,
      map,
      balance,
      emit: (c: Command) => apply(world, c, map, balance),
    };
  }

  const render = await createRenderApp(root, map.size, palette.background);
  const tweens = new Tweens();
  const sun = createSunLayer(map.size, map.projection?.margin ?? 0.7);
  const entities = createEntityLayer(tweens);
  const mapLayer = createMapLayer(map);
  // Tint darkens the terrain only; sky and entities sit above it so lights stay lit.
  render.stage.addChild(mapLayer.root, sun.tint, sun.arc, entities.root);
  render.onScale = (scale) => mapLayer.setScale(scale);
  mapLayer.setScale(render.stage.scale.x);
  const paper = createPaperLayer();
  render.app.stage.addChild(paper.root);
  paper.resize(render.app.screen.width, render.app.screen.height);
  render.app.renderer.on('resize', () =>
    paper.resize(render.app.screen.width, render.app.screen.height),
  );

  const toast = createToast(root);
  const emit = (c: Command): boolean => {
    const r = apply(world, c, map, balance);
    if (!r.ok && r.reason) toast.show(r.reason);
    handleAudioEvents(r.events);
    return r.ok;
  };

  const controller = createController(map, balance, emit);
  render.stage.addChild(controller.ghost);
  entities.onPick = (pick) => controller.onPick(pick, world);
  attachPointer(
    render.app.stage,
    render.stage,
    (x, y) => controller.onMapTap(x, y, world),
    (x, y) => controller.onMapMove(x, y, world),
  );
  render.app.stage.hitArea = render.app.screen;
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') controller.onEscape();
  });

  const hud = createHud(root, emit);
  const curve = createCurveStrip(root, map, balance);
  const shop = createShop(root, balance, (a) => controller.onShop(a));

  const ticksPerDay = balance.time.ticksPerHour * balance.time.hoursPerDay;
  const secondsPerTick = SECONDS_PER_DAY / ticksPerDay;
  let accumulator = 0;
  let over = false;
  const litTowns = new Set<string>();
  const townIdToName = new Map<string, string>();
  for (const t of map.towns) {
    townIdToName.set('town:' + t.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'), t.name);
  }

  render.app.ticker.add((t) => {
    const dtMs = t.deltaMS;
    accumulator += (dtMs / 1000) * world.speed;
    // Cap catch-up so a backgrounded tab does not fast-forward a whole day.
    accumulator = Math.min(accumulator, secondsPerTick * ticksPerDay * 0.25);
    while (accumulator >= secondsPerTick) {
      const events = tick(world, map, balance);
      handleAudioEvents(events);
      if (!over && events.some((e) => e.t === 'GameOver')) {
        over = true;
        showGameOver(root, world, () => window.location.reload());
      }
      accumulator -= secondsPerTick;
    }

    for (const home of world.homes) {
      const name = townIdToName.get(home.townId);
      if (name) litTowns.add(name);
    }

    tweens.update(dtMs);
    // Fractional hour: how far we are into the next tick, so the sky glides.
    const partialTick = world.status === 'running' ? accumulator / secondsPerTick : 0;
    const hourSmooth = (world.time.hour + partialTick / balance.time.ticksPerHour) % 24;
    const sky = sun.update(hourSmooth);
    const dispatch = computeDispatch(world, map, balance);
    entities.sync(world, dispatch, controller.selected, dtMs, 1 - sky.dayness);
    for (const town of map.towns) {
      mapLayer.setTownVisible(town.name, litTowns.has(town.name));
    }
    hud.update(world, hourSmooth);
    curve.update(world);
    shop.update(world, controller.selected, controller.modeName(), controller.hint(world));
  });
}

void main();
